import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import moment from "moment";
import pluralize from "pluralize";
import cx from "classnames";
import _ from "lodash";

import { BannerView } from "src/components/banner";
import Thumbnail from "src/components/thumbnail";
import { ScrollableContainer } from "src/components/scroller";

import { CourseMenuBar } from "../edit/components/menu";
import { CourseLearners } from "../edit/components/learners";
import {
  AssessmentCard,
  LessonCard,
  PodcastCard,
  SectionCard,
  SimulationCard,
} from "../edit/components/cards";
import { getIndexForContent } from "../edit/helper";
import { CourseContext, CourseContextProps } from "../edit/context";
import { CourseNotFound } from "src/pages/coaching/components/notFound";
import { getCourseCreated } from "src/pages/coaching/components/course/view";
import { fetchPublishedCourse, invalidatePublishedCourse } from "src/redux/actions/coaching/courses";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import { Course } from "src/types/coaching/courses";
import { FileService } from "src/utils/file.service";
import userRoleService from "src/utils/userRoles.service";
import {
  getOverviewBackLabel,
  getManageNavigationRootFrom,
  navigateOverviewBack,
  resolveOverviewBackPath,
  COACHING_MANAGE_PATH,
  COACHING_REPORTS_PATH,
} from "src/pages/coaching/utils/manageNavigation";

import styles from "./view.module.scss";

interface MetricsCardProps {
  name: string;
  value?: string;
  subTitle?: string;
}

const MetricsCard: React.FC<MetricsCardProps> = ({ name, value, subTitle }) => (
  <div className={styles.infoCard}>
    <div className={styles.metrics}>
      <span className={styles.name}>{name}</span>
      <span className={styles.value}>{value}</span>
      <span className={styles.subTitle}>{subTitle}</span>
    </div>
  </div>
);

const LeftArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.leftArrow)}>
    <i className="paperflite-arrow-up" />
  </div>
);

const RightArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.rightArrow)}>
    <i className="paperflite-arrow-up" />
  </div>
);

export const CourseOverviewPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const rootFrom = getManageNavigationRootFrom(location.state);
  const params = useParams();
  const id = params.id;

  const course: Course | undefined = useSelector((state: any) =>
    _.get(state, `courses.course[${id}].item`),
  );

  const resolvedBackPath = useMemo(
    () => resolveOverviewBackPath(rootFrom),
    [rootFrom],
  );

  const backLabel = useMemo(
    () => getOverviewBackLabel(rootFrom),
    [rootFrom],
  );

  const loading = useSelector((state: any) =>
    _.get(state, `courses.course[${id}].loading`),
  );

  const courseErrorCode = useSelector((state: any) =>
    _.get(state, `courses.course[${id}].error.errors[0].code`),
  );

  const name = useMemo(() => _.get(course, "name") || "", [course]);
  const description = useMemo(
    () => _.get(course, "description") || "",
    [course],
  );
  const banner = useMemo(() => _.get(course, "banner"), [course]);
  const skills = useMemo(() => _.get(course, "skills", []) || [], [course]);
  const passPercentage = useMemo(
    () => _.get(course, "passPercentage") || 0,
    [course],
  );
  const contents = useMemo(() => course?.contents || [], [course]);
  const publishedBy = useMemo(() => _.get(course, "publishedBy"), [course]);
  const userProfile = useMemo(
    () => _.get(publishedBy, "icon"),
    [publishedBy],
  );
  const firstName = useMemo(
    () => _.get(publishedBy, "firstName", ""),
    [publishedBy],
  );
  const lastName = useMemo(
    () => _.get(publishedBy, "lastName", ""),
    [publishedBy],
  );
  const created = useMemo(
    () => moment(_.get(course, "createdDate")),
    [course],
  );

  const completedLearners = useMemo(
    () => _.get(course, "completedLearners") || 0,
    [course],
  );
  const totalLearners = useMemo(
    () => _.get(course, "totalLearners") || 0,
    [course],
  );
  const passedLearners = useMemo(
    () => _.get(course, "passedLearners") || 0,
    [course],
  );
  const averageScore = useMemo(
    () => _.get(course, "averageScore") || 0,
    [course],
  );
  const completedLearnerAttempts = useMemo(
    () => _.get(course, "completedLearnerAttempts") || 0,
    [course],
  );
  const totalTimeSpent = useMemo(
    () => _.get(course, "totalTimeSpent") || 0,
    [course],
  );
  const averageTimeSpent = useMemo(
    () => _.get(course, "averageTimeSpent") || 0,
    [course],
  );
  const duration = useMemo(() => _.get(course, "duration") || 0, [course]);

  const { lessonCount, assessmentCount } = useMemo(() => {
    let lessons = 0;
    let assessments = 0;
    const lessonTypes = ["lesson", "podcast"];
    const assessmentTypes = ["assessment", "simulation"];

    const countContents = (items: typeof contents) => {
      items.forEach((item) => {
        if (lessonTypes.includes(item.entityType)) {
          lessons++;
        }
        if (assessmentTypes.includes(item.entityType)) {
          assessments++;
        }
        if (item.entityType === "section" && _.size(item.contents) > 0) {
          countContents(item.contents);
        }
      });
    };

    countContents(contents);
    return { lessonCount: lessons, assessmentCount: assessments };
  }, [contents]);

  const getAuthorDetails = (authorFirstName: string, authorLastName: string) =>
    `${authorFirstName} ${authorLastName ?? ""}`.trim();

  const completionInfo = useMemo(() => {
    const rate =
      totalLearners > 0
        ? Math.round((completedLearners / totalLearners) * 100)
        : 0;
    return {
      rate,
      text: `${completedLearners} of ${totalLearners} learners have completed`,
    };
  }, [completedLearners, totalLearners]);

  const avgScoreMetric = useMemo(() => {
    return {
      value:
        completedLearnerAttempts > 0
          ? `${Math.round(averageScore * 100)}%`
          : "na",
      subTitle:
        completedLearnerAttempts > 0
          ? `across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`
          : "No learners have taken the learning yet",
    };
  }, [averageScore, completedLearnerAttempts]);

  const courseForLearners = useMemo(
    () => (course ? { ...course, entityType: "course" as const } : course),
    [course],
  );

  const contextState: CourseContextProps = useMemo(
    () => ({
      id,
      gotoError: () => {},
      showAssetsDialog: false,
      setShowAssetDialog: () => {},
    }),
    [id],
  );

  const handleContentClicked = () => {};

  const onBackClick = () => {
    const fallback =
      userRoleService.canAccessCoachingReport() &&
      !userRoleService.canCreateCourse()
        ? COACHING_REPORTS_PATH
        : COACHING_MANAGE_PATH;

    navigateOverviewBack(navigate, location.state, fallback);
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(invalidatePublishedCourse(id));
    dispatch(fetchPublishedCourse(id));
    dispatch(
      fetchCourseLearners({
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      }),
    );
  }, [id]);

  if (courseErrorCode === 10007) {
    return (
      <CourseNotFound onBack={() => navigateOverviewBack(navigate, location.state, COACHING_MANAGE_PATH)} />
    );
  }

  if (!course || (loading && !course.publishedBy)) {
    return <div className={styles.loading}>Loading course...</div>;
  }

  return (
    <CourseContext.Provider value={contextState}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div className={styles.backButton} onClick={onBackClick}>
              <i className="paperflite-left-arrow" />
              <div>
                <span className={styles.back}>{backLabel}</span>
              </div>
            </div>
          </div>
          <div className={styles.menubar}>
            <CourseMenuBar
              course={course}
              menuState={{ primaryAction: null, secondaryAction: null }}
              onMenuStateChange={() => {}}
              mode="viewer"
            />
          </div>
        </div>

        <div className={styles.editContainer}>
          <div className={styles.left}>
            <div className={styles.details}>
              <div className={cx(styles.field, styles.name)}>
                <div className={styles.courseTitle}>{name}</div>
              </div>

              <div className={cx(styles.field, styles.thumbnail)}>
                <div className={styles.inner}>
                  <BannerView
                    banner={banner as any}
                    defaultBanner="/images/assets/courseThumbnail.jpg"
                    overlay={false}
                  />
                </div>
              </div>

              <div className={styles.courseMetadata}>
                <div className={styles.metadata}>
                  <i className="paperflite-file" />
                  {lessonCount} {pluralize("lesson", lessonCount)}
                </div>
                <div className={styles.metadata}>
                  <i className="paperflite-document" />
                  {assessmentCount}{" "}
                  {pluralize("assessment", assessmentCount)}
                </div>
                {duration > 0 && (
                  <div className={styles.metadata}>
                    <i className="paperflite-clock" />
                    {FileService.formatDuration(duration, "long")}
                  </div>
                )}
                {firstName && (
                  <div className={cx(styles.metadata, styles.profile)}>
                    <Thumbnail
                      media={userProfile}
                      defaultImage="/images/assets/default_asset.jpg"
                      className={styles.icon}
                    />
                    <span className={styles.details}>
                      by {getAuthorDetails(firstName, lastName)},{" "}
                      {getCourseCreated(created)}
                    </span>
                  </div>
                )}
              </div>

              <div className={cx(styles.field, styles.description)}>
                <div className={styles.label}>Description</div>
                <div
                  className={styles.readonlyDescription}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                />
              </div>

              {_.size(contents) > 0 && (
                <div className={cx(styles.field, styles.contents)}>
                  <div className={styles.label}>Course Content</div>
                  <div className={styles.contentsList}>
                    {contents.map((content, index) => {
                      const type = _.get(content, "entityType");
                      return (
                        <React.Fragment key={content.uuid}>
                          {type === "lesson" && (
                            <LessonCard
                              lessonIndex={getIndexForContent(
                                contents,
                                content.uuid,
                                content.parent,
                              )}
                              lesson={content}
                              index={index}
                              active={false}
                              mode="view"
                              readonly
                              onClick={handleContentClicked}
                            />
                          )}
                          {type === "assessment" && (
                            <AssessmentCard
                              assessment={content}
                              assessmentIndex={getIndexForContent(
                                contents,
                                content.uuid,
                                content.parent,
                              )}
                              index={index}
                              active={false}
                              mode="view"
                              readonly
                              onClick={handleContentClicked}
                            />
                          )}
                          {type === "podcast" && (
                            <PodcastCard
                              asset={content}
                              mode="view"
                              index={index}
                              active={false}
                              lessonIndex={getIndexForContent(
                                contents,
                                content.uuid,
                                content.parent,
                              )}
                              onClick={handleContentClicked}
                            />
                          )}
                          {type === "simulation" && (
                            <SimulationCard
                              asset={content}
                              mode="view"
                              index={index}
                              active={false}
                              lessonIndex={getIndexForContent(
                                contents,
                                content.uuid,
                                content.parent,
                              )}
                              onClick={handleContentClicked}
                            />
                          )}
                          {type === "section" && (
                            <SectionCard
                              section={content}
                              index={index}
                              sectionIndex={getIndexForContent(
                                contents,
                                content.uuid,
                                content.parent,
                              )}
                              active={false}
                              mode="view"
                              readonly
                              onClick={handleContentClicked}
                            />
                          )}
                          <div className={styles.dividerLine} />
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.right}>
            <ScrollableContainer
              distance={300}
              leftArrowComponent={LeftArrow}
              rightArrowComponent={RightArrow}
            >
              <div className={styles.infoContainer}>
                <MetricsCard
                  name="completion rate"
                  value={`${completionInfo.rate}%`}
                  subTitle={completionInfo.text}
                />
                <MetricsCard
                  name="pass rate"
                  value={`${Math.round(course?.passRate ?? 0)}%`}
                  subTitle={`${passedLearners} of ${completedLearners} ${pluralize("completed learners", completedLearners)} have passed`}
                />
                <MetricsCard
                  name="avg score"
                  value={avgScoreMetric.value}
                  subTitle={avgScoreMetric.subTitle}
                />
                <MetricsCard
                  name="total time spent"
                  value={FileService.formatDuration(totalTimeSpent, "short")}
                  subTitle={`across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`}
                />
                <MetricsCard
                  name="avg time spent"
                  value={FileService.formatDuration(averageTimeSpent, "short")}
                  subTitle={`across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`}
                />
              </div>
            </ScrollableContainer>

            <div className={styles.skillsContainer}>
              <p className={styles.heading}>
                skills learners will gain ({skills.length})
              </p>
              <div className={styles.skills}>
                {skills.map((skill: { id?: string; name?: string }) => (
                  <div key={skill.id} className={styles.skill}>
                    <div className={styles.name}>{skill.name}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.passPercentageContainer}>
              <p className={styles.heading}>Pass percentage to get:</p>
              <p className={styles.passValue}>{passPercentage}%</p>
              <p className={styles.subText}>
                This represents the minimum pass percentage the learner must
                achieve for this course.
              </p>
            </div>

            <div className={styles.courseLearnersContainer}>
              {courseForLearners && course.id && (
                <CourseLearners
                  id={course.id}
                  course={courseForLearners}
                  navigationFrom={location.pathname}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </CourseContext.Provider>
  );
};
